import Lottie from 'lottie-react';
import * as React from 'react';

import buddyHoverAnimation from '@/assets/animations/buddyHover.json';
import confettiAnimation from '@/assets/animations/confetti.json';
import speakingDotsAnimation from '@/assets/animations/speakingDots.json';

export const BhanzuBuddyEmptyState: React.FC<{
  currentMode: 'explain' | 'guide';
}> = ({ currentMode }) => {
  const messages = {
    explain: {
      title: "Hi there! I'm BhanzuBuddy! 👋",
      subtitle: 'Your AI learning companion',
      description:
        "I'm here to help you understand mathematical concepts, solve problems, and make learning fun! Ask me anything about math, and I'll explain it step by step.",
      tips: [
        '🔢 Ask me to solve equations',
        '📊 Explain mathematical concepts',
        '🧮 Help with calculations',
        '📝 Show working steps',
      ],
    },
    guide: {
      title: 'Ready to learn together? 🎯',
      subtitle: 'Your step-by-step learning guide',
      description:
        "I'm your personal math tutor! I'll guide you through problems step by step, helping you understand each concept as we go. Let's start your learning journey!",
      tips: [
        '👆 I will guide you step-by-step',
        '🎯 Interactive problem solving',
        '💡 Learn by doing',
        '🏆 Build confidence as you learn',
      ],
    },
  } as const;

  const currentMessages = messages[currentMode];

  return (
    <div className="flex flex-col items-center justify-center h-full min-h-96 text-center px-4 sm:px-8">
      <div className="relative mb-6 overflow-hidden">
        <div className="absolute inset-0 -inset-4">
          <Lottie
            animationData={confettiAnimation}
            loop={true}
            autoplay={true}
            className="w-full h-full opacity-60"
          />
        </div>

        <div className="relative z-10 w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-2 rounded-full bg-magical-moonlight-d3 border-2 border-magical-moonlight-d2 flex items-center justify-center">
          <Lottie
            animationData={buddyHoverAnimation}
            loop={true}
            autoplay={true}
            className="w-16 h-16 sm:w-20 sm:h-20"
          />
        </div>

        <div className="flex justify-center">
          <div className="w-8 h-8">
            <Lottie
              animationData={speakingDotsAnimation}
              loop={true}
              autoplay={true}
              className="w-full h-full"
            />
          </div>
        </div>
      </div>

      <div className="space-y-4 max-w-md mx-auto">
        <h2 className="text-2xl sm:text-3xl font-bold text-white">
          {currentMessages.title}
        </h2>

        <p className="text-lg text-blue-chaos font-medium">
          {currentMessages.subtitle}
        </p>

        <p className="text-magical-moonlight-white leading-relaxed">
          {currentMessages.description}
        </p>

        <div className="space-y-2 mt-6">
          {currentMessages.tips.map((tip, index) => (
            <div
              key={index}
              className="flex items-center justify-start text-sm text-magical-moonlight-d1"
              style={{
                animation: `fadeInUp 0.6s ease-out ${index * 0.1}s both`,
              }}
            >
              <span className="mr-2">{tip}</span>
            </div>
          ))}
        </div>

        <div className="mt-8 p-4 bg-gradient-to-r from-blue-chaos/10 to-bhanzu-orange/10 rounded-xl border border-blue-chaos/20">
          <p className="text-white font-medium mb-2">
            ✨ Ready to start learning?
          </p>
          <p className="text-sm text-magical-moonlight-d1">
            Type your question below or click the microphone to speak!
          </p>
        </div>
      </div>

      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};
